/* eslint-disable react/prop-types */
/**
 * Iter 228 — QuickActionsLauncher.
 *
 * The `⊕` button in the top banner (desktop + mobile) that opens a
 * dropdown of on-the-fly actions, gated by the user's perms + the
 * tenant's owned modules. This component IS the canonical 1-tap
 * surface for: clock in/out, + ticket, + JC, + lead, + task, camera,
 * voice note, scan slip, Engineering Desk, picking.
 *
 * Trust Contract — NO modals. Renders as a slide-down inline
 * dropdown panel; closes on outside-click + Esc.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Clock, FileText, ClipboardList, GitBranch, CheckSquare,
  Camera, Mic, ScanText, FlaskConical, PackageCheck, X,
  LifeBuoy, Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";

// Canonical Quick Actions catalogue. Each item is module + perm gated.
const ACTIONS = [
  {
    key: "clock", label: "Clock in / out", icon: Clock,
    href: "/clock", perm: "nav.dashboard", module: null,
    description: "Start or end your shift",
  },
  {
    key: "new_ticket", label: "+ Ticket", icon: LifeBuoy,
    href: "/tickets?new=1", perm: "action.edit_tickets", module: "support",
    description: "Log a support ticket",
  },
  {
    key: "new_jc", label: "+ Job card", icon: ClipboardList,
    href: "/jobcards?new=1", perm: "action.edit_jobcards", module: "ops",
    description: "Create a job card",
  },
  {
    key: "new_lead", label: "+ Lead", icon: GitBranch,
    href: "/leads?new=1", perm: "action.edit_leads", module: "sales_flow",
    description: "Capture a new lead",
  },
  {
    key: "new_task", label: "+ Task", icon: CheckSquare,
    href: "/tasks?new=1", perm: "nav.tasks", module: null,
    description: "Add a task for yourself or the team",
  },
  {
    key: "camera", label: "Take photo", icon: Camera,
    href: "/upload?source=camera", perm: "nav.dashboard", module: null,
    description: "Capture and tag a job photo",
  },
  {
    key: "voice_note", label: "Voice note", icon: Mic,
    href: "/hidro?voice=1", perm: "ai.hidro_voice", module: null,
    description: "Dictate a note — auto-routed by Hidro",
  },
  {
    key: "scan_slip", label: "Scan slip", icon: ScanText,
    href: "/ocr/invoices?new=1", perm: "tools.ocr", module: "bookkeep",
    description: "OCR a supplier slip or invoice",
  },
  {
    key: "eng_desk", label: "Engineering Desk", icon: FlaskConical,
    href: "/tools/engineering-desk", perm: "nav.tools", module: "ops",
    description: "Deep research workspace",
  },
  {
    key: "picking", label: "Picking", icon: PackageCheck,
    href: "/picking", perm: "nav.tools", module: "stockroom",
    description: "Picking workspace",
  },
  {
    key: "coach", label: "Coach check-in", icon: Sparkles,
    event: "hv:open-coach", perm: "nav.dashboard", module: null,
    description: "Open your personal Hidro Coach",
  },
];

export default function QuickActionsLauncher({
  compact = false,
  testId,
}) {
  const { user, hasPermission } = useAuth();
  const { modules } = useBranding();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  // Outside click + Esc to close
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  const hasModule = (m) => {
    if (!m) return true;
    if (!modules || Object.keys(modules).length === 0) return true; // hydrating
    return modules[m] !== false;
  };

  // Filter actions by user's role perms + tenant's module ownership
  const visible = ACTIONS.filter(
    (a) => hasPermission(a.perm) && hasModule(a.module),
  );

  if (visible.length === 0) return null;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId || "quick-actions-launcher"}
        title="Quick actions"
        aria-label="Quick actions"
        aria-expanded={open}
        className={
          "flex items-center gap-1.5 rounded-full transition-colors " +
          (compact
            ? "p-1.5 text-[#a0aabf] hover:text-white hover:bg-white/5"
            : "px-3 py-1.5 text-[#a0aabf] hover:text-white hover:bg-white/5 border border-white/10 hover:border-white/25")
        }
      >
        <Plus className={compact ? "w-4 h-4" : "w-4 h-4"} strokeWidth={2.5} />
        {!compact && <span className="text-xs font-medium">Quick actions</span>}
      </button>

      {open && (
        <div
          data-testid="quick-actions-menu"
          className="absolute right-0 top-full mt-2 w-72 max-w-[calc(100vw-2rem)] z-[80] bg-[#121419] border border-white/15 rounded-xl shadow-2xl py-2 hv-fade-in"
        >
          <div className="px-3 py-1.5 flex items-center justify-between border-b border-white/10 mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#647087]">
              Quick actions
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[#647087] hover:text-white"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {visible.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => {
                  setOpen(false);
                  if (a.event) {
                    window.dispatchEvent(new CustomEvent(a.event));
                  } else if (a.href) {
                    navigate(a.href);
                  }
                }}
                data-testid={`qa-${a.key}`}
                className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-white/5 transition-colors group"
              >
                <a.icon className="w-4 h-4 mt-0.5 text-[#1fb6c1] shrink-0" strokeWidth={1.8} />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-white">{a.label}</span>
                  {a.description && (
                    <span className="block text-[11px] text-[#647087] truncate">
                      {a.description}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
